import { FiInfo, FiLayers, FiTarget } from 'react-icons/fi'
import { RiTextSnippet } from 'react-icons/ri'
import { TbRoute } from 'react-icons/tb'
import { MapRef } from 'react-map-gl/mapbox'

import { getCenterFromBounds, zoomToCenter } from '@/lib/map/geo'

import { useTrackerMapContext } from './TrackerMapContext'

interface TrackerMapLayerTogglesProps {
  mapRef: React.RefObject<MapRef | null>
}

/**
 * Vertical map controls (top-left, inside the map), mirroring the asset map's
 * MapLayerToggles: recenter on majority, toggle clustering, toggle labels.
 */
export const TrackerMapLayerToggles = ({ mapRef }: TrackerMapLayerTogglesProps) => {
  const context = useTrackerMapContext()

  const handleRecenter = () => {
    const map = mapRef.current?.getMap()
    if (!map) return

    // Selected tracker → fly to it; otherwise frame the densest cluster.
    if (context.currentTrackerData?.location?.latitude != null) {
      const { latitude, longitude } = context.currentTrackerData.location
      map.flyTo({ center: [longitude as number, latitude], zoom: 14, duration: 1000 })
      return
    }
    const points = context.filteredTrackers
      .filter((t) => t.location)
      .map((t) => ({ latitude: t.location!.latitude, longitude: t.location!.longitude }))
    if (points.length === 0) return
    const { bounds, zoom } = zoomToCenter(points, 0, 15)
    context.setMapState((prev) => ({ ...prev, bounds, zoom }))
    const center = getCenterFromBounds(bounds)
    map.flyTo({ center: [center.longitude, center.latitude], zoom, duration: 1000 })
  }

  return (
    <div className="absolute top-4 left-4 z-10 md:flex flex-col gap-1 hidden">
      <button
        onClick={handleRecenter}
        className="tooltip tooltip-right tooltip-primary btn btn-sm btn-square bg-base-100 shadow-lg border border-base-300 hover:bg-base-200"
        data-tip="Recenter map on majority of trackers"
      >
        <FiTarget className="w-4 h-4" />
      </button>
      <button
        data-tip="Toggle tracker clustering on/off"
        onClick={() => context.setMapState((prev) => ({ ...prev, clustersEnabled: !prev.clustersEnabled }))}
        className={`tooltip tooltip-right tooltip-primary btn btn-sm btn-square shadow-lg border border-base-300 ${
          context.mapState.clustersEnabled ? 'btn-primary' : 'bg-base-100 hover:bg-base-200'
        }`}
      >
        <FiLayers className="w-4 h-4" />
      </button>
      <button
        data-tip="Toggle tracker labels on/off"
        onClick={() => context.setMapState((prev) => ({ ...prev, trackerLabels: !prev.trackerLabels }))}
        className={`tooltip tooltip-right tooltip-primary btn btn-sm btn-square shadow-lg border border-base-300 ${
          context.mapState.trackerLabels ? 'btn-primary' : 'bg-base-100 hover:bg-base-200'
        }`}
      >
        <RiTextSnippet className="w-4 h-4" />
      </button>
      {context.currentRowId != null && (
        <>
          <div className="h-px bg-base-300 my-1" />
          {context.currentRow?.trackerId != null && context.currentRow?.actualStartAt != null && (
            <button
              data-tip="Show shipment location history"
              onClick={() => context.setHistoryMode(!context.historyMode)}
              className={`tooltip tooltip-right tooltip-primary btn btn-sm btn-square shadow-lg border border-base-300 ${
                context.historyMode ? 'btn-primary' : 'bg-base-100 hover:bg-base-200'
              }`}
            >
              <TbRoute className="w-4 h-4" />
            </button>
          )}
          <button
            id="quick-view-toggle"
            data-tip="Toggle tracker quick view"
            onClick={() => context.setMapState((prev) => ({ ...prev, showQuickView: !prev.showQuickView }))}
            className={`tooltip tooltip-right tooltip-primary btn btn-sm btn-square shadow-lg border border-base-300 ${
              context.mapState.showQuickView ? 'btn-primary' : 'bg-base-100 hover:bg-base-200'
            }`}
          >
            <FiInfo className="w-4 h-4" />
          </button>
        </>
      )}
    </div>
  )
}
